import React, { lazy, Suspense } from 'react';
import type { ComponentType } from 'react';
import { Box, CircularProgress } from '@mui/material';

interface LazyComponentOptions {
  fallback?: React.ReactNode;
  preload?: boolean;
}

// Default loading fallback
const DefaultFallback = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: 200,
      width: '100%',
    }}
  >
    <CircularProgress />
  </Box>
);

// Factory function to create lazy components with preload capability
export function createLazyComponent<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T } | T>,
  options: LazyComponentOptions = {}
): React.LazyExoticComponent<T> & { preload: () => Promise<void> } {
  const { preload = false } = options;

  // Create the lazy component
  const LazyComponent = lazy(async () => {
    const module = await importFn();
    return 'default' in module ? module : { default: module };
  });

  // Add preload method
  (LazyComponent as any).preload = async () => {
    await importFn();
  };

  // Auto-preload if requested
  if (preload && typeof window !== 'undefined') {
    // Preload after initial render
    requestIdleCallback(() => {
      (LazyComponent as any).preload();
    }, { timeout: 2000 });
  }

  return LazyComponent as React.LazyExoticComponent<T> & { preload: () => Promise<void> };
}

// HOC to wrap lazy components with suspense
export function withLazyLoading<P extends object>(
  Component: React.LazyExoticComponent<ComponentType<P>>,
  fallback?: React.ReactNode
): React.FC<P> {
  return (props: P) => (
    <Suspense fallback={fallback || <DefaultFallback />}>
      <Component {...(props as any)} />
    </Suspense>
  );
}

// Preload critical components on idle
export function preloadComponents(
  components: Array<{ preload: () => Promise<void> }>
): void {
  if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
    requestIdleCallback(() => {
      components.forEach(component => {
        component.preload().catch(err => {
          console.warn('Failed to preload component:', err);
        });
      });
    }, { timeout: 3000 });
  }
}

// Helper to lazy load AG Grid components
export function lazyLoadGrid<P extends object>(
  importPath: string,
  componentName: string
): React.LazyExoticComponent<ComponentType<P>> & { preload: () => Promise<void> } {
  return createLazyComponent<ComponentType<P>>(
    () => import(/* @vite-ignore */ importPath).then(module => ({ default: module[componentName] })),
    { preload: true }
  );
}

// Intersection Observer hook for lazy loading on visibility
export function useLazyLoadOnVisible(
  ref: React.RefObject<HTMLElement>,
  onIntersect: () => void,
  options: IntersectionObserverInit = {}
): void {
  React.useEffect(() => {
    if (!ref.current) return;

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            onIntersect();
            observer.disconnect();
          }
        });
      },
      { threshold: 0.1, ...options }
    );

    observer.observe(ref.current);

    return () => observer.disconnect();
  }, [ref, onIntersect]);
}